import React, { useState } from 'react';
import { Filter, X } from 'lucide-react';
import { TopbarSearch } from '@/components/ui/TopbarSearch';

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterDefinition {
  id: string;
  label: string;
  options: FilterOption[];
}

interface DataFilterToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  
  filterDefs: FilterDefinition[];
  filterValues: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  
  actions?: React.ReactNode;
}

export function DataFilterToolbar({
  search,
  onSearchChange,
  searchPlaceholder = 'Tìm kiếm...',
  filterDefs,
  filterValues,
  onFilterChange,
  actions,
}: DataFilterToolbarProps) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Computed active filters for the chips
  const activeFilters = filterDefs
    .filter(def => filterValues[def.id] && filterValues[def.id] !== '' && filterValues[def.id] !== 'all')
    .map(def => {
      const selectedOption = def.options.find(opt => opt.value === filterValues[def.id]);
      return {
        id: def.id,
        label: selectedOption?.label || filterValues[def.id] // Fallback to raw value
      };
    });

  const handleClearAll = () => {
    filterDefs.forEach(def => onFilterChange(def.id, ''));
  };

  return (
    <div className="flex flex-col gap-3 bg-white border border-slate-200 rounded-lg shadow-sm p-3">
      {/* ROW 1: Search & Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Left: Global Search & Mobile Filter Toggle */}
        <div className="flex items-center gap-2 w-full md:w-[400px]">
          <div className="flex-1">
            <TopbarSearch
              placeholder={searchPlaceholder}
              value={search}
              onChange={onSearchChange}
              className="w-full h-9 bg-slate-50 border border-slate-200 text-xs"
            />
          </div>
          {filterDefs.length > 0 && (
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className={`md:hidden h-9 px-3 flex items-center justify-center gap-2 rounded-md border transition-colors shrink-0 ${
                isFilterOpen ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-slate-200 text-slate-600'
              }`}
            >
              <Filter className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right: Actions */}
        {actions && (
          <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar">
            {actions}
          </div>
        )}
      </div>

      {/* ROW 2: Select Filters */}
      {filterDefs.length > 0 && (
        <div className={`grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 ${isFilterOpen ? 'grid' : 'hidden md:grid'}`}>
          {filterDefs.map(def => (
            <div key={def.id} className="relative w-full">
              <select
                value={filterValues[def.id] || ''}
                onChange={(e) => onFilterChange(def.id, e.target.value)}
                className="w-full h-9 appearance-none px-3 pr-8 rounded-md border border-slate-200 text-xs font-medium bg-slate-50 text-slate-700 focus:outline-none focus:border-emerald-500 hover:border-emerald-500 transition-colors cursor-pointer"
              >
                <option value="">{def.label}</option>
                {def.options.map((opt, idx) => (
                  <option key={idx} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          ))}
        </div>
      )}

      {/* ROW 3: Filter Chips */}
      {activeFilters.length > 0 && (
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <div className="flex flex-wrap items-center gap-2">
            {activeFilters.map(filter => (
              <span key={filter.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-200 cursor-pointer">
                {filter.label}
                <X className="w-3 h-3 text-slate-400 hover:text-slate-700" onClick={() => onFilterChange(filter.id, '')} />
              </span>
            ))}
          </div>
          <button
            onClick={handleClearAll}
            className="text-[11px] font-medium text-slate-500 hover:text-rose-600 transition-colors px-2 py-1 rounded-md hover:bg-rose-50 shrink-0"
          >
            Xóa tất cả ✕
          </button>
        </div>
      )}
    </div>
  );
}
